import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const AdminBookings = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [roomTypes, setRoomTypes] = useState({});
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookings();
  }, [filter]);

  const loadBookings = async () => {
    try {
      const [bookingsData, roomTypesData] = await Promise.all([
        api.get(`/api/admin/bookings${filter ? `?status=${filter}` : ''}`, token),
        api.get('/api/room-types'),
      ]);
      
      setBookings(bookingsData);
      
      const roomTypesMap = {};
      roomTypesData.forEach(rt => {
        roomTypesMap[rt.id] = rt;
      });
      setRoomTypes(roomTypesMap);
    } catch (error) {
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const csvContent = [
      ['ID', 'Tipo Quarto', 'Check-in', 'Check-out', 'Hóspedes', 'Total', 'Status'],
      ...bookings.map(b => [
        b.id,
        roomTypes[b.room_type_id]?.name || 'N/A',
        b.check_in,
        b.check_out,
        b.guests,
        b.total_price,
        b.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reservas-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exportado com sucesso');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_payment: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmada', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-800' },
      refunded: { label: 'Reembolsada', className: 'bg-blue-100 text-blue-800' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-bookings-page">
      <AdminSidebar />
      
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-display font-bold text-primary">
            Gestão de Reservas
          </h1>
          <Button onClick={exportCSV} data-testid="export-csv-button">
            Exportar CSV
          </Button>
        </div>

        <div className="mb-6 flex gap-3">
          <Button
            variant={filter === '' ? 'default' : 'outline'}
            onClick={() => setFilter('')}
            data-testid="filter-all"
          >
            Todas
          </Button>
          <Button
            variant={filter === 'confirmed' ? 'default' : 'outline'}
            onClick={() => setFilter('confirmed')}
            data-testid="filter-confirmed"
          >
            Confirmadas
          </Button>
          <Button
            variant={filter === 'pending_payment' ? 'default' : 'outline'}
            onClick={() => setFilter('pending_payment')}
            data-testid="filter-pending"
          >
            Pendentes
          </Button>
          <Button
            variant={filter === 'cancelled' ? 'default' : 'outline'}
            onClick={() => setFilter('cancelled')}
            data-testid="filter-cancelled"
          >
            Canceladas
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-primary/5">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Quarto</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Check-in</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Check-out</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Hóspedes</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Total</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50" data-testid={`booking-row-${booking.id}`}>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {booking.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {roomTypes[booking.room_type_id]?.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {format(new Date(booking.check_in), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {format(new Date(booking.check_out), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-4 py-3 text-sm">{booking.guests}</td>
                    <td className="px-4 py-3 text-sm font-semibold">
                      R$ {booking.total_price.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getStatusBadge(booking.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bookings.length === 0 && (
              <div className="text-center py-12 text-text-secondary">
                Nenhuma reserva encontrada.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
